import React from 'react';
import { Link } from 'react-router-dom';
import { Shield, Lock, Eye, Database, Cookie, Trash2 } from 'lucide-react';

export default function Privacy() {
  return (
    <main className="min-h-screen bg-gradient-to-br from-[#102318] via-[#183326] to-[#0b1710] text-white pt-20 px-4 pb-16 font-[Cormorant_Garamond]">
      <section className="max-w-5xl mx-auto">
        <p className="text-[#ffcc32] font-black uppercase text-sm tracking-widest">Legal</p>
        <h1 className="text-5xl md:text-7xl uppercase mt-2 mb-8" style={{ fontFamily: 'Cinzel, serif', letterSpacing: "0.1em" }}>
          Privacy Policy
        </h1>
        
        <div className="bg-black/30 border border-white/10 rounded-lg p-8 mb-8">
          <p className="text-zinc-400 leading-relaxed">
            At RRide Moto Studio, we take your privacy seriously. This Privacy Policy explains how we collect, use, and protect your personal information when you use our website and services.
          </p>
        </div>

        <div className="space-y-8 text-zinc-300">
          <div className="bg-black/20 border border-white/5 rounded-lg p-6">
            <div className="flex items-center gap-3 mb-4">
              <Database className="text-[#ffcc32]" size={24} />
              <h2 className="text-2xl font-bold text-white uppercase">Information We Collect</h2>
            </div>
            <p className="leading-relaxed mb-4">
              We collect the following information to provide you with our services:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Name</li>
              <li>Mobile Number</li>
              <li>Email Address</li>
              <li>Shipping Address</li>
              <li>Order Details</li>
            </ul>
          </div>

          <div className="bg-black/20 border border-white/5 rounded-lg p-6">
            <div className="flex items-center gap-3 mb-4">
              <Lock className="text-[#ffcc32]" size={24} />
              <h2 className="text-2xl font-bold text-white uppercase">Payment Security</h2>
            </div>
            <p className="leading-relaxed">
              All payment information is securely processed by <span className="text-white font-semibold">Razorpay</span>, a leading payment gateway in India. We do not store your complete payment details (such as credit/debit card numbers) on our servers. Razorpay handles all payment processing with bank-grade security.
            </p>
          </div>

          <div className="bg-black/20 border border-white/5 rounded-lg p-6">
            <div className="flex items-center gap-3 mb-4">
              <Shield className="text-[#ffcc32]" size={24} />
              <h2 className="text-2xl font-bold text-white uppercase">Authentication & Sessions</h2>
            </div>
            <p className="leading-relaxed">
              We use secure JWT (JSON Web Token) based authentication for user sessions. Your authentication tokens are stored securely and are used to verify your identity when accessing your account and placing orders.
            </p>
          </div>

          <div className="bg-black/20 border border-white/5 rounded-lg p-6">
            <div className="flex items-center gap-3 mb-4">
              <Cookie className="text-[#ffcc32]" size={24} />
              <h2 className="text-2xl font-bold text-white uppercase">Cookies</h2>
            </div>
            <p className="leading-relaxed">
              We may use cookies only for authentication purposes and to improve website functionality. Cookies help us maintain your session and provide a better user experience. We do not use cookies for intrusive tracking or advertising.
            </p>
          </div>

          <div className="bg-black/20 border border-white/5 rounded-lg p-6">
            <div className="flex items-center gap-3 mb-4">
              <Eye className="text-[#ffcc32]" size={24} />
              <h2 className="text-2xl font-bold text-white uppercase">Marketing Communications</h2>
            </div>
            <p className="leading-relaxed">
              We do not send promotional emails or SMS messages without your explicit consent. You will only receive essential communications related to your orders, such as order confirmations, shipping updates, and transaction messages.
            </p>
          </div>

          <div className="bg-black/20 border border-white/5 rounded-lg p-6">
            <div className="flex items-center gap-3 mb-4">
              <Trash2 className="text-[#ffcc32]" size={24} />
              <h2 className="text-2xl font-bold text-white uppercase">Data Deletion</h2>
            </div>
            <p className="leading-relaxed">
              You have the right to request deletion of your account and personal data. To request data deletion, please contact our support team at <span className="text-[#ffcc32]">rridemods@gmail.com</span>. We will process your request within a reasonable time frame, subject to legal and regulatory requirements.
            </p>
          </div>

          <div className="bg-black/20 border border-white/5 rounded-lg p-6">
            <h2 className="text-2xl font-bold text-white uppercase mb-4">Data Sharing</h2>
            <p className="leading-relaxed">
              We do not sell, rent, or share your personal information with third parties for marketing purposes. Your information may only be shared with:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4 mt-4">
              <li>Shipping carriers for order delivery</li>
              <li>Payment processors (Razorpay) for transaction processing</li>
              <li>Service providers who assist in operating our website</li>
            </ul>
            <p className="leading-relaxed mt-4">
              All third-party service providers are contractually obligated to protect your data and use it only for the specific purposes for which it was shared.
            </p>
          </div>

          <div className="bg-black/20 border border-white/5 rounded-lg p-6">
            <h2 className="text-2xl font-bold text-white uppercase mb-4">Data Security</h2>
            <p className="leading-relaxed">
              We implement appropriate technical and organizational measures to protect your personal data against unauthorized access, alteration, disclosure, or destruction. However, no method of transmission over the internet is 100% secure, and we cannot guarantee absolute security.
            </p>
          </div>

          <div className="bg-black/20 border border-white/5 rounded-lg p-6">
            <h2 className="text-2xl font-bold text-white uppercase mb-4">Your Rights</h2>
            <p className="leading-relaxed mb-4">
              You have the following rights regarding your personal data:
            </p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Right to access your personal data</li>
              <li>Right to correct inaccurate data</li>
              <li>Right to request deletion of your data</li>
              <li>Right to object to processing of your data</li>
              <li>Right to withdraw consent</li>
            </ul>
          </div>

          <div className="bg-black/20 border border-white/5 rounded-lg p-6">
            <h2 className="text-2xl font-bold text-white uppercase mb-4">Changes to This Policy</h2>
            <p className="leading-relaxed">
              We may update this Privacy Policy from time to time. We will notify you of any significant changes by posting the new policy on our website and updating the "Last Updated" date.
            </p>
          </div>
        </div>

        <div className="mt-12 bg-gradient-to-r from-[#ffcc32]/10 to-transparent border border-[#ffcc32]/20 rounded-lg p-6">
          <h3 className="text-xl font-bold text-white uppercase mb-3">Contact Us</h3>
          <p className="text-zinc-400 mb-4">
            If you have any questions about this Privacy Policy or our data practices, please contact us:
          </p>
          <div className="space-y-2 text-sm">
            <p><span className="text-[#ffcc32]">Email:</span> rridemods@gmail.com</p>
            <p><span className="text-[#ffcc32]">Phone:</span> +91 8793678780</p>
            <p><span className="text-[#ffcc32]">Address:</span> Ground Floor, Mahaveer Building, Mandai - Dhamankar Naka Road, Near Payal Talkies, Narpoli, Bhiwandi, Maharashtra 421305, India</p>
          </div>
        </div>

        <div className="mt-8 text-center text-zinc-500 text-sm">
          <p>Last Updated: July 2026</p>
        </div>
      </section>
    </main>
  );
}